import { useTranslator } from '@bluebeam/react-i18n';
import Button from 'components/Button';
import HasPermissions from 'components/Permissions/HasPermissions';
import { FeatureFlag, useFeatureFlags } from 'featureFlags';
import { useIntegrationsCheck } from 'hooks/useIntegrationsCheck';
import largeDrawings from 'images/large-drawings.svg';
import React, { FC, useEffect } from 'react';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import ImportDrawingsDropdown from './Imports/ImportDrawingsDropdown';

interface Props extends WithAppStoreProps {
  onUploadDrawings: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => void;
  disabled?: boolean;
}

const NoDrawings: FC<Props> = ({
  appState,
  actions,
  onUploadDrawings,
  disabled,
}) => {
  const { translate } = useTranslator(['noDrawings', 'toastText']);
  const { studioProjects } = appState;

  const { [FeatureFlag.ImportFromStudio]: isImportFromStudioEnabled } =
    useFeatureFlags();

  const canImportFromStudio =
    isImportFromStudioEnabled && studioProjects.size > 0;

  const { areIntegrationsEnabled } = useIntegrationsCheck();

  const initialSetup = async () => {
    const toastText = {
      fileUploadError: {
        title: translate('toastText:fileUploadError.title'),
        message: translate('toastText:fileUploadError.message'),
      },
      drawingsReadyForReview: {
        title: translate('toastText:drawingsReadyForReview.title'),
        message: translate('toastText:drawingsReadyForReview.message'),
      },
      fileUploadSuccess: {
        title: translate('toastText:fileUploadSuccess.title'),
        message: translate('toastText:fileUploadSuccess.message'),
      },
    };

    actions.getProcessingStatuses(toastText);
  };

  useEffect(() => {
    initialSetup();
  }, []);

  return (
    <div
      id="no-drawings"
      className="column items-center bgd-light-grey-2 pt9 bt-2"
      style={{ height: 'calc(100vh - 168px)' }}
    >
      <div className="mt1 mb4 fill-gray-5">
        <img
          src={largeDrawings}
          alt="Large submittal illustration"
          className="b-radius-100"
        />
      </div>
      <div className="font-size-7 color-gray-9 mb2">
        {translate('noDrawings:noDrawings')}
      </div>
      <HasPermissions permissions={['publishDrawings']}>
        <div
          className="font-size-5 color-gray-6 mb7 center"
          style={{ width: '260px' }}
        >
          {areIntegrationsEnabled
            ? translate('noDrawings:integrationsEnabled')
            : translate('noDrawings:integrationsDisabled')}
        </div>
        <div className="column">
          <Button
            id="no-drawings-upload-btn"
            onClick={onUploadDrawings}
            size="lg"
            variant="primary"
            disabled={disabled}
          >
            {translate('noDrawings:uploadDrawings')}
          </Button>
          {(areIntegrationsEnabled || canImportFromStudio) && (
            <div className="mt4">
              <ImportDrawingsDropdown disabled={disabled} />
            </div>
          )}
        </div>
      </HasPermissions>
    </div>
  );
};

export default withAppStore<Props>(NoDrawings);
